import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { RegisterSplitLayout } from '../../components/register/RegisterSplitLayout';
import { REGISTER_VISUALS } from '../../components/register/registerVisuals';
import { useRegistrationOnboardingGuard } from '../../lib/hooks/useRegistrationOnboardingGuard';
import { trackRegisterStepViewed } from '../../lib/analytics/onboardingEvents';

const SOURCES = [
  { key: 'google',       label: 'Ricerca Google' },
  { key: 'passaparola',  label: 'Passaparola' },
  { key: 'linkedin',     label: 'LinkedIn' },
  { key: 'meta',         label: 'Instagram / Facebook' },
  { key: 'youtube',      label: 'YouTube' },
  { key: 'podcast',      label: 'Podcast' },
  { key: 'ai',           label: 'Assistente AI' },
  { key: 'altro',        label: 'Altro' },
] as const;

/** Step 6: "Come ci hai conosciuto?" — scelta singola, skippabile.
 *  Salva su organizations.onboarding_profile (migration 20260715). */
export function DiscoveryPage() {
  const navigate = useNavigate();
  const { ready, orgId } = useRegistrationOnboardingGuard();
  const [selected, setSelected] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { trackRegisterStepViewed('discovery'); }, []);

  const goNext = () => navigate('/register/connect');

  const handleContinue = async () => {
    if (!selected || !orgId) { goNext(); return; }
    setSaving(true);
    try {
      // Best-effort: se la colonna non esiste ancora (migration non applicata)
      // si prosegue comunque — lo step non deve mai bloccare la registrazione.
      const { error } = await supabase
        .from('organizations')
        .update({ onboarding_profile: { how_found: selected, collected_at: new Date().toISOString() } })
        .eq('id', orgId);
      if (error) console.warn('[Discovery] salvataggio onboarding_profile fallito:', error.message);
    } finally {
      setSaving(false);
      goNext();
    }
  };

  if (!ready) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#787774]" />
      </div>
    );
  }

  return (
    <RegisterSplitLayout visual={REGISTER_VISUALS.discovery} step={6}>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[#37352F] mb-1">Come ci hai conosciuto?</h1>
        <p className="text-[#787774] text-sm">Ci aiuta a capire dove trovarci — una sola risposta</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {SOURCES.map(({ key, label }) => {
          const active = selected === key;
          return (
            <button key={key} type="button"
              onClick={() => setSelected(active ? null : key)}
              className={`px-3.5 py-2 rounded-full text-sm border transition-all ${
                active
                  ? 'border-[#2383E2] bg-[rgba(35,131,226,0.06)] text-[#2383E2] font-medium'
                  : 'border-[rgba(55,53,47,0.16)] text-[#37352F] hover:border-[rgba(55,53,47,0.3)] hover:bg-[rgba(55,53,47,0.03)]'
              }`}>
              {label}
            </button>
          );
        })}
      </div>
      <div className="mt-8 space-y-3">
        <button type="button" onClick={handleContinue} disabled={saving} className="notion-register-btn w-full">
          <span>Continua</span>
          <ArrowRight className="w-4 h-4" />
        </button>
        <button type="button" onClick={goNext}
          className="w-full text-center text-sm text-[#787774] hover:text-[#37352F] transition-colors py-1.5">
          Salta
        </button>
      </div>
    </RegisterSplitLayout>
  );
}
